import { compressImageBlob } from './compressImage';

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export interface PreparedFilePart {
  type: 'file';
  mediaType: string;
  url: string;
}

/** Why a submit could not carry its attachments. Maps 1:1 to shopper-facing copy. */
export type PrepareAttachmentsFailure = 'unsupported-type' | 'unreadable';

export type PrepareAttachmentsResult =
  | { ok: true; fileParts: PreparedFilePart[] }
  | { ok: false; reason: PrepareAttachmentsFailure };

export interface PrepareAttachmentsDeps {
  /** Fetch the bytes behind a `blob:`/`data:` URL. Injected so tests need no network. */
  readBlob: (url: string) => Promise<Blob>;
  compress: (blob: Blob, mediaType: string) => Promise<{ url: string; mediaType: string }>;
}

const defaultDeps: PrepareAttachmentsDeps = {
  readBlob: async (url) => (await fetch(url)).blob(),
  compress: compressImageBlob,
};

/**
 * Turn the composer's attachments into the file parts the chat request carries.
 *
 * All-or-nothing on purpose. The previous inline version skipped any file it
 * could not handle and let the submit proceed, which is how PetArmor QA row 52
 * ended up filing a pricing complaint whose screenshots had quietly fallen off
 * — the shopper saw their message send and had no way to know the evidence was
 * missing. A caller that gets `ok: false` must not send the turn.
 *
 * A `blob:` URL is only valid while the browser still holds the object, and iOS
 * reclaims those under memory pressure and on backgrounding — precisely what
 * happens while a large HEIC is decoding. So a read failure here is expected
 * traffic, not an impossible case, and it has to be reported rather than
 * swallowed.
 */
export async function prepareAttachments(
  files: Array<{ url?: string; mediaType?: string }> | undefined,
  deps: PrepareAttachmentsDeps = defaultDeps,
): Promise<PrepareAttachmentsResult> {
  if (!files?.length) return { ok: true, fileParts: [] };

  // Type-check the whole set before doing any decoding work, so an obviously
  // wrong file fails instantly instead of after several seconds of compression.
  for (const file of files) {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.mediaType || '')) {
      return { ok: false, reason: 'unsupported-type' };
    }
  }

  const fileParts: PreparedFilePart[] = [];
  for (const file of files) {
    const url = file.url;
    const mediaType = file.mediaType || 'image/jpeg';

    if (!url) return { ok: false, reason: 'unreadable' };

    if (url.startsWith('http')) {
      fileParts.push({ type: 'file', mediaType, url });
      continue;
    }

    try {
      const blob = await deps.readBlob(url);
      // Downscale before inlining. A full-resolution phone photo is several MB,
      // base64 adds another third, and the request then fails as an opaque
      // "connection error" — the iOS symptom in the QA log.
      const { url: compressedUrl, mediaType: compressedType } = await deps.compress(blob, mediaType);
      fileParts.push({ type: 'file', mediaType: compressedType, url: compressedUrl });
    } catch {
      return { ok: false, reason: 'unreadable' };
    }
  }

  return { ok: true, fileParts };
}

/** Shopper-facing copy for a failed preparation. */
export function attachmentFailureMessage(reason: PrepareAttachmentsFailure): string {
  return reason === 'unsupported-type'
    ? 'Please upload a valid image file (JPEG, PNG, WebP, or GIF)'
    : "That image couldn't be attached. Try adding it again — your message wasn't sent.";
}
